import React from 'react';
import { REPORT } from '../print/PrintAtoms';
import { acRange } from './slopeRange';

interface SlopeHistogramProps {
  pctUnder5: number | null;
  pctUnder10: number | null;
  pctUnder15: number | null;
  // Crown Pine A1: the props above are the 3 m grid; this is the 5 m end of the range.
  // The bars stay on 3 m (the conservative end) and the acreage line shows the range.
  at5m?: { pctUnder5: number; pctUnder10: number; pctUnder15: number };
  acreage?: number | null;
  areaLabel?: string;
  landownerEarthwork?: boolean;
}

// Cumulative → per-bin. Clamp negatives from float noise.
function deriveBins(p5: number, p10: number, p15: number) {
  return {
    under5: p5,
    five_to_ten: Math.max(0, p10 - p5),
    ten_to_fifteen: Math.max(0, p15 - p10),
    over_15: Math.max(0, 100 - p15),
  };
}

export const SlopeHistogram: React.FC<SlopeHistogramProps> = ({
  pctUnder5, pctUnder10, pctUnder15, at5m, acreage, landownerEarthwork, areaLabel = 'parcel',
}) => {
  if (pctUnder5 == null || pctUnder10 == null || pctUnder15 == null) {
    return (
      <div style={{ fontSize: 10, color: REPORT.tertiary, padding: '6px 0' }}>
        Slope histogram computing — will appear after next enrichment pass.
      </div>
    );
  }

  const bins = deriveBins(Number(pctUnder5), Number(pctUnder10), Number(pctUnder15));
  const WIDTH = 500;
  const HEIGHT = 48;
  const BAR_Y = 14;
  const BAR_H = 16;
  const LEFT = 0;

  type Seg = { label: string; pct: number; color: string; note: string; opacity: number };
  // 4-tier breakout: pass (full) for buildable, pass (lighter) for light-grading,
  // caution for moderate, fail for heavy. The lighter-green is the same pass
  // hex with reduced opacity — stays inside the palette while reading as
  // "still acceptable" rather than "warning".
  const segments: Seg[] = [
    { label: '<5%',    pct: bins.under5,         color: REPORT.pass,    note: 'buildable',         opacity: 0.85 },
    { label: '5–10%',  pct: bins.five_to_ten,    color: REPORT.pass,    note: 'light grading',     opacity: 0.45 },
    { label: '10–15%', pct: bins.ten_to_fifteen, color: REPORT.caution, note: 'moderate grading',  opacity: 0.75 },
    { label: '>15%',   pct: bins.over_15,        color: REPORT.fail,    note: 'heavy earthwork',   opacity: 0.70 },
  ];

  let xCursor = LEFT;
  const totalW = WIDTH;

  return (
    <div style={{ marginBottom: 8 }}>
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} width="100%" style={{ display: 'block' }}>
        {segments.map((seg, i) => {
          const w = (seg.pct / 100) * totalW;
          const xStart = xCursor;
          xCursor += w;
          if (w < 0.5) return null;
          return (
            <g key={i}>
              <rect x={xStart} y={BAR_Y} width={w} height={BAR_H}
                    fill={seg.color} fillOpacity={seg.opacity} />
              {w > 26 && (
                <text x={xStart + w / 2} y={BAR_Y + BAR_H / 2 + 3}
                      fill="#fff" fontSize={8} textAnchor="middle" fontWeight={600}>
                  {seg.pct.toFixed(0)}%
                </text>
              )}
            </g>
          );
        })}

        {(() => {
          let cx = LEFT;
          return segments.map((seg, i) => {
            const w = (seg.pct / 100) * totalW;
            const labelX = cx + w / 2;
            cx += w;
            if (w < 36) return null;
            return (
              <text key={`lbl-${i}`} x={labelX} y={BAR_Y - 3}
                    fill={REPORT.secondary} fontSize={7.5} textAnchor="middle" fontWeight={600}>
                {seg.label}
              </text>
            );
          });
        })()}

        <text x={LEFT} y={HEIGHT - 3} fill={REPORT.tertiary} fontSize={7}>
          {at5m ? `% of ${areaLabel} area by slope grade · 3 m grid; acreages span the 3–5 m grids` : <>% of {areaLabel} area by slope grade · USGS 3DEP DEM</>}
        </text>
      </svg>
      {acreage != null && acreage > 0 && (
        <div style={{ fontSize: 10, color: REPORT.secondary, marginTop: 4, lineHeight: 1.4 }}>
          <strong>{at5m ? acRange((Number(pctUnder5) / 100) * acreage, (at5m.pctUnder5 / 100) * acreage) : Math.round((bins.under5 / 100) * acreage).toLocaleString()} ac</strong> pad-ready (&lt;5%)
          {' · '}
          <strong>{at5m ? acRange((Number(pctUnder10) / 100) * acreage, (at5m.pctUnder10 / 100) * acreage) : Math.round(((bins.under5 + bins.five_to_ten) / 100) * acreage).toLocaleString()} ac</strong> with light grading (≤10%)
          {' · '}
          <strong>{at5m ? acRange((Number(pctUnder15) / 100) * acreage, (at5m.pctUnder15 / 100) * acreage) : Math.round(((bins.under5 + bins.five_to_ten + bins.ten_to_fifteen) / 100) * acreage).toLocaleString()} ac</strong> with standard grading (≤15%)
          {landownerEarthwork && (
            <div style={{ fontStyle: 'italic', color: REPORT.pass, marginTop: 2 }}>
              + Landowner grading commitment may expand further — operator to scope.
            </div>
          )}
        </div>
      )}
    </div>
  );
};
